import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import {
  FileCheck, Gavel, HeartPulse, ChevronRight, Download,
  Building2, TrendingUp, TrendingDown, Shield, FileText
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";

export default function DashboardHome() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dashboard/stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error("Stats fetch error:", e);
    }
  }, [API]);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/events`, { credentials: "include" });
      if (res.ok) setEvents(await res.json());
    } catch (e) {
      console.error(e);
    }
  }, [API]);

  useEffect(() => {
    fetchStats();
    fetchEvents();
  }, [fetchStats, fetchEvents]);

  const totalProps = stats?.total_properties || 0;
  const legalClear = stats?.legal_clear || 0;
  const underLit = stats?.under_litigation || 0;
  const pendingVal = stats?.pending_valuation || 0;

  // Donut chart data
  const donutData = [
    { label: "Legal Clear", value: legalClear || 1, color: "#D4AF37" },
    { label: "Under Litigation", value: underLit || 0, color: "#F59E0B" },
    { label: "Pending Valuation", value: pendingVal || 0, color: "#556B2F" },
  ];

  const total = donutData.reduce((s, d) => s + d.value, 0);
  let cumAngle = 0;
  const donutSegments = donutData.map((d) => {
    const angle = (d.value / total) * 360;
    const startAngle = cumAngle;
    cumAngle += angle;
    return { ...d, startAngle, angle };
  });

  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const rad = (a) => ((a - 90) * Math.PI) / 180;
    const x1 = cx + r * Math.cos(rad(startAngle));
    const y1 = cy + r * Math.sin(rad(startAngle));
    const x2 = cx + r * Math.cos(rad(endAngle));
    const y2 = cy + r * Math.sin(rad(endAngle));
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const quickCards = [
    { icon: FileCheck, label: "Title Report Requests", count: stats?.title_report_requests || 0, color: "#D4AF37", path: "/dashboard/title-reports" },
    { icon: Gavel, label: "Court Case Updates", count: stats?.court_case_updates || 0, color: "#F59E0B", path: "/dashboard/court-cases" },
    { icon: HeartPulse, label: "Valuation Health Reports", count: totalProps, color: "#556B2F", path: "/dashboard/valuation" },
  ];

  const downloadReport = () => {
    window.open(`${API}/reports/pdf`, "_blank");
  };

  return (
    <div data-testid="dashboard-home" className="space-y-4 md:space-y-6">
      {/* Welcome + Calendar Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Welcome Card */}
        <div className="lg:col-span-2 bg-[#3F4A2E]/40 border border-[#3F4A2E]/60 rounded-sm p-6 relative overflow-hidden">
          <div className="relative z-10">
            <h2 data-testid="welcome-message" className="font-serif text-2xl sm:text-3xl text-white mb-6">
              Welcome back, <span className="text-[#D4AF37]">{user?.name?.split(" ")[0] || "User"}</span>
            </h2>
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Donut Chart */}
              <div className="flex items-center gap-4">
                <div className="relative w-28 h-28">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    {donutSegments.map((seg, i) => (
                      <path
                        key={i}
                        d={describeArc(50, 50, 38, seg.startAngle, seg.startAngle + Math.max(seg.angle - 2, 0.5))}
                        fill="none"
                        stroke={seg.color}
                        strokeWidth="8"
                        strokeLinecap="round"
                      />
                    ))}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-white font-serif text-2xl">{totalProps}</span>
                    <span className="text-white/40 text-[10px] font-sans uppercase tracking-wider">Total</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {donutData.map((d) => (
                    <div key={d.label} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-white/60 text-xs font-sans">{d.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Property illustration placeholder */}
              <div className="hidden sm:flex items-center justify-center flex-1">
                <Building2 className="w-20 h-20 text-white/10" />
              </div>
            </div>
            <p className="text-white/40 text-xs font-sans mt-4">Last updated: just now</p>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-4">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            className="w-full [&_.rdp-day_button]:text-white/70 [&_.rdp-day_button.rdp-day_selected]:bg-[#D4AF37] [&_.rdp-day_button.rdp-day_selected]:text-black [&_.rdp-head_cell]:text-white/40 [&_.rdp-caption_label]:text-white [&_.rdp-nav_button]:text-white/40"
          />
          <div className="mt-3 space-y-2">
            {events.length > 0 ? events.slice(0, 2).map((evt) => (
              <div key={evt.event_id} className="flex items-center gap-3 bg-white/5 rounded-sm p-2">
                <span className="text-[#D4AF37] text-xs font-sans font-medium">{evt.time || "TBD"}</span>
                <div>
                  <p className="text-white text-xs font-sans">{evt.title}</p>
                  <p className="text-white/40 text-[10px] font-sans">{evt.description}</p>
                </div>
              </div>
            )) : (
              <div className="text-white/30 text-xs font-sans text-center py-2">No upcoming events</div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {quickCards.map((card) => (
          <button
            key={card.label}
            data-testid={`quick-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
            onClick={() => navigate(card.path)}
            className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors text-left group flex items-center justify-between"
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: card.color + "20" }}>
                  <card.icon className="w-4 h-4" style={{ color: card.color }} />
                </div>
                <span className="text-white/50 text-xs font-sans">{card.label}</span>
              </div>
              <span className="font-serif text-3xl text-white">{card.count}</span>
            </div>
            <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
          </button>
        ))}
      </div>

      {/* Valuation Chart + Side Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Valuation Chart */}
        <div className="lg:col-span-2 bg-[#11130F] border border-white/10 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-serif text-lg text-white">Property Valuation Overview</h3>
              <p className="text-white/40 text-xs font-sans">Your property finance report Jan 24 - Dec 24</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats?.monthly_data || []} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip
                    contentStyle={{ background: "#11130F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 }}
                    labelStyle={{ color: "#FAFAFA" }}
                  />
                  <Bar dataKey="income" radius={[2, 2, 0, 0]} fill="#556B2F" />
                  <Bar dataKey="expense" radius={[2, 2, 0, 0]} fill="#D4AF37" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              <div>
                <p className="font-serif text-2xl text-white">${(stats?.total_valuation || 200000).toLocaleString()}</p>
                <p className="text-white/40 text-xs font-sans">Total Balance</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#556B2F]/20 rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#556B2F]" />
                </div>
                <div>
                  <p className="text-white text-sm font-sans">$1,000</p>
                  <p className="text-white/40 text-[10px] font-sans">Total Income</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-sm flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white text-sm font-sans">$1,000</p>
                  <p className="text-white/40 text-[10px] font-sans">Total Expense</p>
                </div>
              </div>
              <button
                data-testid="download-report-btn"
                onClick={downloadReport}
                className="w-full flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-sm py-2 text-white/60 text-xs font-sans hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors"
              >
                <Download className="w-3 h-3" />
                Download Report
              </button>
            </div>
          </div>
        </div>

        {/* Right Side Cards */}
        <div className="space-y-4">
          {/* Legal Case Status */}
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-serif text-base text-white">Legal Case Status</h4>
              <button
                data-testid="legal-case-more-btn"
                onClick={() => navigate("/dashboard/court-cases")}
                className="text-[#D4AF37] text-xs font-sans hover:underline"
              >
                More
              </button>
            </div>
            <p className="text-white/50 text-xs font-sans mb-2">{stats?.court_case_updates || 0} Properties Involved in Legal Cases</p>
            <p className="font-serif text-4xl text-[#D4AF37]">{stats?.resolved_cases || 0}</p>
            <p className="text-white/50 text-xs font-sans mt-1">Resolved Cases</p>
          </div>

          {/* Valuation Health Summary */}
          <div className="bg-[#3F4A2E]/20 border border-[#3F4A2E]/40 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-serif text-base text-white">Valuation Health</h4>
              <button
                data-testid="valuation-health-more-btn"
                onClick={() => navigate("/dashboard/valuation")}
                className="text-[#D4AF37] text-xs font-sans hover:underline"
              >
                More
              </button>
            </div>
            <p className="font-serif text-4xl text-[#556B2F]">{stats?.valuation_health_pct || 0}%</p>
            <p className="text-white/50 text-xs font-sans mt-1">of properties have Excellent Valuation Health</p>
          </div>
        </div>
      </div>

      {/* Bottom Row: PIO + Documents + Notifications */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          data-testid="quick-card-pio"
          onClick={() => navigate("/dashboard/pio")}
          className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors text-left group flex items-center justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-[#556B2F]/20">
                <Shield className="w-4 h-4 text-[#556B2F]" />
              </div>
              <span className="text-white/50 text-xs font-sans">Active PIO Assignments</span>
            </div>
            <span className="font-serif text-3xl text-white">{stats?.pio_assignments || 0}</span>
          </div>
          <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
        </button>
        <button
          data-testid="quick-card-documents"
          onClick={() => navigate("/dashboard/documents")}
          className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors text-left group flex items-center justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-[#D4AF37]/20">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <span className="text-white/50 text-xs font-sans">Property Documents</span>
            </div>
            <span className="font-serif text-3xl text-white">{stats?.documents_count || 0}</span>
          </div>
          <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
        </button>
        <button
          data-testid="quick-card-notifications"
          onClick={() => navigate("/dashboard/pio")}
          className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors text-left group flex items-center justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-[#F59E0B]/20">
                <Gavel className="w-4 h-4 text-[#F59E0B]" />
              </div>
              <span className="text-white/50 text-xs font-sans">Unread Notifications</span>
            </div>
            <span className="font-serif text-3xl text-white">{stats?.unread_notifications || 0}</span>
          </div>
          <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
        </button>
      </div>
    </div>
  );
}
